import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Toolbar,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Popover,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import devicesData from '../data/devices.json';
import DeviceTable from './devices/DeviceTable';
import DeviceForm from './devices/DeviceForm';
import ConfirmDialog from './devices/ConfirmDialog';

type Device = {
  id: string;
  name: string;
  owner: string;
  type: string;
  status: string;
  updated: string;
};

const Devices: React.FC = () => {
  const [query, setQuery] = React.useState('');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  // Load devices from JSON into mutable state so we can add rows
  const [devices, setDevices] = React.useState<Device[]>(() => devicesData as Device[]);

  // Column visibility
  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'name', label: 'Device Name' },
    { key: 'owner', label: 'Device Owner' },
    { key: 'type', label: 'Device Type' },
    { key: 'status', label: 'Status' },
    { key: 'updated', label: 'Last Updated' },
  ];

  const VCOLS_KEY = 'racs-visible-columns';
  const [visibleColumns, setVisibleColumns] = React.useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem(VCOLS_KEY);
      if (raw) return JSON.parse(raw) as Record<string, boolean>;
    } catch (e) {}
    const map: Record<string, boolean> = {};
    columns.forEach((c) => (map[c.key] = true));
    return map;
  });

  // New device dialog
  const [newOpen, setNewOpen] = React.useState(false);
  const [newDevice, setNewDevice] = React.useState<Device>({ id: '', name: '', owner: '', type: '', status: '', updated: '' });
  const [editOpen, setEditOpen] = React.useState(false);
  const [editDevice, setEditDevice] = React.useState<Device | null>(null);
  const [deleteId, setDeleteId] = React.useState<string | null>(null);

  // Column filter (per-column multi-value)
  const [filterAnchor, setFilterAnchor] = React.useState<HTMLElement | null>(null);
  const [filterColumn, setFilterColumn] = React.useState<string>('');
  const [filterValues, setFilterValues] = React.useState<Record<string, string[]>>({});

  // Column visibility popover
  const [colsAnchor, setColsAnchor] = React.useState<HTMLElement | null>(null);

  // Filter logic (global query + per-column multi-value filters)
  const filtered = devices.filter((d) => {
    const q = query.toLowerCase();
    const globalMatch =
      d.id.toLowerCase().includes(q) || d.name.toLowerCase().includes(q) || d.owner.toLowerCase().includes(q);

    // per-column filters: for each column that has selections, row must match one of them
    const colsMatch = Object.entries(filterValues).every(([col, vals]) => {
      if (!vals || vals.length === 0) return true;
      const cell = String((d as any)[col] ?? '').toLowerCase();
      return vals.some((v) => cell === v.toLowerCase());
    });

    return globalMatch && colsMatch;
  });

  // Pagination logic
  const visible = filtered.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const openNew = () => setNewOpen(true);
  const closeNew = () => setNewOpen(false);

  const handleAddDevice = () => {
    if (!newDevice.id) return;
    setDevices((prev) => [newDevice, ...prev]);
    setNewDevice({ id: '', name: '', owner: '', type: '', status: '', updated: '' });
    closeNew();
  };

  const openEdit = (d: Device) => {
    setEditDevice(d);
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditDevice(null);
  };

  const handleUpdateDevice = () => {
    if (!editDevice) return;
    setDevices((prev) => prev.map((p) => (p.id === editDevice.id ? editDevice : p)));
    closeEdit();
  };

  const openDelete = (id: string) => setDeleteId(id);
  const closeDelete = () => setDeleteId(null);

  const handleConfirmDelete = () => {
    if (!deleteId) return;
    setDevices((prev) => prev.filter((d) => d.id !== deleteId));
    closeDelete();
  };

  const openFilter = (e: React.MouseEvent<HTMLElement>) => setFilterAnchor(e.currentTarget);
  const closeFilter = () => setFilterAnchor(null);

  const openCols = (e: React.MouseEvent<HTMLElement>) => setColsAnchor(e.currentTarget);
  const closeCols = () => setColsAnchor(null);

  const toggleColumn = (key: string) => {
    setVisibleColumns((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(VCOLS_KEY, JSON.stringify(next)); } catch (e) {}
      return next;
    });
  };

  // helper: unique values for a column
  const uniqueValuesFor = (col: string) => {
    const set = new Set<string>();
    devices.forEach((d) => {
      const v = (d as any)[col];
      if (v !== undefined && v !== null) set.add(String(v));
    });
    return Array.from(set).sort();
  };

  const handleToggleFilterValue = (col: string, value: string) => {
    setFilterValues((prev) => {
      const arr = prev[col] ? [...prev[col]] : [];
      const idx = arr.indexOf(value);
      if (idx === -1) arr.push(value); else arr.splice(idx, 1);
      const next = { ...prev, [col]: arr };
      return next;
    });
  };

  return (

    <Box sx={{ p: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" sx={{ color: '#e0e0e0' }}>
          Devices
        </Typography>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openNew} sx={{ textTransform: 'none' }}>
          New Device
        </Button>
      </Box>

      {/* Toolbar */}
      <Paper sx={{ mb: 1, backgroundColor: 'transparent', boxShadow: 'none' }}>
        <Toolbar sx={{ px: 0, gap: 2, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button variant="contained" sx={{ textTransform: 'none' }}>
              Groups ▾
            </Button>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              placeholder="Search Devices"
              size="small"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#9e9e9e' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: 360,
                backgroundColor: '#151515',
                borderRadius: 1,
                input: { color: '#fff' },
              }}
            />

            <IconButton color="inherit" onClick={openFilter}>
              <FilterListIcon sx={{ color: '#9aa0a6' }} />
            </IconButton>

            <IconButton color="inherit" onClick={openCols}>
              <ViewColumnIcon sx={{ color: '#9aa0a6' }} />
            </IconButton>

            
          </Box>
        </Toolbar>
      </Paper>

      {/* Table */}
      <Paper sx={{ bgcolor: '#141414', color: '#e0e0e0', borderRadius: 1, p: 1 }}>
        <DeviceTable
          columns={columns}
          visibleColumns={visibleColumns}
          rows={visible}
          page={page}
          rowsPerPage={rowsPerPage}
          count={filtered.length}
          onEdit={openEdit}
          onDelete={openDelete}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>

      {/* New Device Dialog */}
      <Dialog open={newOpen} onClose={closeNew} fullWidth maxWidth="sm">
        <DialogTitle>New Device</DialogTitle>
        <DialogContent>
          <DeviceForm device={newDevice} onChange={(d) => setNewDevice(d)} />
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-start', gap: 2 }}>
          <Button onClick={closeNew}>Cancel</Button>
          <Button onClick={handleAddDevice} variant="contained" sx={{ backgroundColor: '#1976d2',textTransform: 'none', px: 4, py: 1.5, fontWeight: 700 }}>Submit</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Device Dialog */}
      <Dialog open={editOpen} onClose={closeEdit} fullWidth maxWidth="sm">
        <DialogTitle>Edit Device</DialogTitle>
        <DialogContent>
          <DeviceForm device={editDevice} onChange={(d) => setEditDevice(d)} />
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-start', gap: 2 }}>
          <Button onClick={closeEdit}>Cancel</Button>
          <Button onClick={handleUpdateDevice} variant="contained" sx={{ backgroundColor: '#1976d2',textTransform: 'none', px: 4, py: 1.5, fontWeight: 700 }}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog open={Boolean(deleteId)} title="Confirm Delete" message="Are you sure you want to delete this device?" onClose={closeDelete} onConfirm={handleConfirmDelete} />

      {/* Filter Popover */}
      <Popover
        open={Boolean(filterAnchor)}
        anchorEl={filterAnchor}
        onClose={closeFilter}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, width: 320, display: 'grid', gap: 1 }}>
          {/* <FormControl fullWidth size="small">
            <InputLabel>Column</InputLabel>
            <Select value={filterColumn} label="Column" onChange={(e) => setFilterColumn(e.target.value)}>
              <MenuItem value="">(none)</MenuItem>
              {columns.map((c) => (
                <MenuItem key={c.key} value={c.key}>{c.label}</MenuItem>
              ))}
            </Select>
          </FormControl> */}

          {filterColumn ? (
            <Box sx={{ maxHeight: 240, overflow: 'auto', mt: 1 }}>
              {uniqueValuesFor(filterColumn).map((val) => (
                <FormControlLabel
                  key={val}
                  control={<Checkbox checked={(filterValues[filterColumn] || []).includes(val)} onChange={() => handleToggleFilterValue(filterColumn, val)} />}
                  label={val}
                />
              ))}
            </Box>
          ) : (
            <Box sx={{ mt: 1, color: 'text.secondary' }}>Choose a column to filter values</Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button onClick={() => { if (filterColumn) setFilterValues(prev => ({ ...prev, [filterColumn]: [] })); closeFilter(); }}>Clear</Button>
            <Button variant="contained" onClick={closeFilter}>Apply</Button>
          </Box>
        </Box>
      </Popover>

      {/* Columns Popover */}
      <Popover
        open={Boolean(colsAnchor)}
        anchorEl={colsAnchor}
        onClose={closeCols}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Box sx={{ p: 2 }}>
          {columns.map((c) => (
            <FormControlLabel
              key={c.key}
              control={<Checkbox checked={visibleColumns[c.key]} onChange={() => toggleColumn(c.key)} />}
              label={c.label}
            />
          ))}
        </Box>
      </Popover>
    </Box>
  );
};

export default Devices;
